import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useLocation } from "react-router-dom";

import useResetPassword from "../../mutation/resetPassword/resetPassword";

import Swal from "sweetalert2";

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, "Password must contain at least 8 characters")
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])/,
      "Password must contain at least 1 uppercase, lowercase character and number"
    )
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Password does not match!")
    .required("Confirm Password is required"),
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPassword = (props) => {
  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(schema),
  });

  let query = useQuery();

  const [resetCode, setResetCode] = useState(query.get("code"));

  const [response, setData] = useResetPassword();

  const onSubmit = (values) => {
    Swal.fire({
      title: "Loading..",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      showCancelButton: false,
      showCloseButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
      didOpen: async () => {
        setData({
          code: resetCode,
          password: values.newPassword,
          passwordConfirmation: values.confirmPassword,
        });
      },
    });
  };

  useEffect(() => {
    if (response.data) {
      props.history.push("/", { passwordChanged: true });
      Swal.close();
    } else if (response.error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: response.errorMessage.message,
      });
    }
  }, [response]);

  return (
    <Grid container component="main" sx={{ minHeight: "100vh" }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={5}
        sx={{
          backgroundImage: "url(img/login.jpg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(160deg, rgba(0,186,178,0.35) 0%, rgba(13,45,44,0.45) 100%)",
          },
        }}
      />
      <Grid
        item
        xs={12}
        sm={8}
        md={7}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#fbfcfc",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            mx: "auto",
            px: { xs: 3, sm: 4 },
            py: { xs: 6, sm: 8 },
          }}
        >
          <Box
            component="img"
            src="img/logo.png"
            alt="YSuites"
            sx={{ height: 48, mb: 2.5, display: "block" }}
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{
              color: "primary.main",
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Middleware
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a new password for your account.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Typography component="h2" variant="h5" sx={{ mb: 1 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please enter your new Password.
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="Password"
              type="password"
              id="password"
              inputRef={register}
              error={errors.newPassword != null}
              helperText={
                errors.newPassword != null ? errors.newPassword.message : null
              }
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              inputRef={register}
              error={errors.confirmPassword != null}
              helperText={
                errors.confirmPassword != null
                  ? errors.confirmPassword.message
                  : null
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.25 }}
            >
              Reset Password
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
